import { isJsonbRepresentableString } from "../../contracts/src/schemas/shared/jsonb-string.ts";
import { isContentDocument } from "./content.ts";
import { isFiniteNumber, isNullableString, isRecord } from "./predicates.ts";
import type {
  CheckboxGroupFieldDefinition,
  ChoiceFieldDefinition,
  ConfirmFieldDefinition,
  ContentFieldDefinition,
  DateTimeFieldDefinition,
  FormbrewSubmissionResult,
  HiddenFieldDefinition,
  PublicFormDefinition,
  PublicFormField,
  RadioGroupFieldDefinition,
  SelectFieldDefinition,
} from "./types.ts";

type DataEnvelope = {
  data: unknown;
};

type ErrorEnvelope = {
  details?: unknown;
  error: string;
  message: string;
};

type BoundedIntegerResult = {
  valid: boolean;
  value?: number;
};

type LengthRuleRecord = {
  maxLength: unknown;
  minLength: unknown;
};

const HIDDEN_DEFAULT_VALUE_LIMIT = 10_000;
const NUMBER_MAX_DECIMAL_PLACES_LIMIT = 15;

const dateTimeModes = new Set(["both", "date", "time"]);

// Fields whose stored value may be duplicated by a CONFIRM field's target.
const confirmTargetTypes = new Set(["TEXT", "EMAIL", "PHONE", "WEBSITE", "NUMBER", "DATETIME"]);

const fieldTypes = new Set([
  "TEXT",
  "EMAIL",
  "NUMBER",
  "PHONE",
  "WEBSITE",
  "CHECKBOX",
  "CHECKBOX_GROUP",
  "RADIO_GROUP",
  "SELECT",
  "DATETIME",
  "HIDDEN",
  "CONFIRM",
  "CONTENT",
]);

export const isPublicFormDefinition = (value: unknown): value is PublicFormDefinition => {
  if (!isRecord(value)) {
    return false;
  }

  if (
    typeof value.id !== "string" ||
    typeof value.name !== "string" ||
    typeof value.slug !== "string" ||
    !Number.isInteger(value.schemaVersion) ||
    typeof value.submitButtonLabel !== "string" ||
    typeof value.successMessage !== "string" ||
    typeof value.honeypotFieldName !== "string" ||
    !Array.isArray(value.fields)
  ) {
    return false;
  }

  if (!value.fields.every(isPublicFormField)) {
    return false;
  }

  return hasValidConfirmTargets(value.fields);
};

export const isSubmissionResult = (value: unknown): value is FormbrewSubmissionResult => {
  if (!isRecord(value)) {
    return false;
  }

  return typeof value.id === "string" && typeof value.message === "string";
};

export const isDataEnvelope = (value: unknown): value is DataEnvelope => {
  return isRecord(value) && Object.hasOwn(value, "data");
};

export const isErrorEnvelope = (value: unknown): value is ErrorEnvelope => {
  if (!isRecord(value)) {
    return false;
  }

  return typeof value.error === "string" && typeof value.message === "string";
};

export const isChoiceField = (field: PublicFormField): field is ChoiceFieldDefinition => {
  return field.type === "CHECKBOX_GROUP" || field.type === "RADIO_GROUP" || field.type === "SELECT";
};

export const isCheckboxGroupField = (
  field: PublicFormField,
): field is CheckboxGroupFieldDefinition => {
  return field.type === "CHECKBOX_GROUP";
};

export const isRadioGroupField = (field: PublicFormField): field is RadioGroupFieldDefinition => {
  return field.type === "RADIO_GROUP";
};

export const isSelectField = (field: PublicFormField): field is SelectFieldDefinition => {
  return field.type === "SELECT";
};

export const isDateTimeField = (field: PublicFormField): field is DateTimeFieldDefinition => {
  return field.type === "DATETIME";
};

export const isHiddenField = (field: PublicFormField): field is HiddenFieldDefinition => {
  return field.type === "HIDDEN";
};

export const isConfirmField = (field: PublicFormField): field is ConfirmFieldDefinition => {
  return field.type === "CONFIRM";
};

export const isContentField = (field: PublicFormField): field is ContentFieldDefinition => {
  return field.type === "CONTENT";
};

const isPublicFormField = (value: unknown): value is PublicFormField => {
  if (!isPublicFormFieldBase(value)) {
    return false;
  }

  switch (value.type) {
    case "TEXT":
      return isTextConfig(value.configJson) && isLengthValidation(value.validationJson, 10_000);
    case "EMAIL":
      return value.configJson === null && isLengthValidation(value.validationJson, 10_000);
    case "PHONE":
      return value.configJson === null && isLengthValidation(value.validationJson, 64);
    case "NUMBER":
      return value.configJson === null && isNumberValidation(value.validationJson);
    case "WEBSITE":
      return value.configJson === null && isWebsiteValidation(value.validationJson);
    case "CHECKBOX":
      return value.configJson === null && value.validationJson === null;
    case "CHECKBOX_GROUP":
      return isChoiceConfig(value.configJson) && isSelectionValidation(value.validationJson);
    case "RADIO_GROUP":
    case "SELECT":
      return isChoiceConfig(value.configJson) && value.validationJson === null;
    case "DATETIME":
      return isDateTimeConfig(value.configJson) && value.validationJson === null;
    case "HIDDEN":
      return (
        isHiddenConfig(value.configJson) &&
        value.validationJson === null &&
        value.required === false
      );
    case "CONFIRM":
      return (
        isConfirmConfig(value.configJson) &&
        value.validationJson === null &&
        value.required === false
      );
    case "CONTENT":
      return (
        isContentConfig(value.configJson) &&
        value.validationJson === null &&
        value.required === false
      );
    default:
      return false;
  }
};

/** Validates that every CONFIRM field targets an allowed field elsewhere in the form. */
const hasValidConfirmTargets = (fields: unknown[]): boolean => {
  const typesById = new Map<string, string>();
  for (const field of fields) {
    if (isRecord(field) && typeof field.id === "string" && typeof field.type === "string") {
      typesById.set(field.id, field.type);
    }
  }

  for (const field of fields) {
    if (!isRecord(field) || field.type !== "CONFIRM" || !isRecord(field.configJson)) {
      continue;
    }

    const targetId = field.configJson.targetFieldId;
    if (typeof targetId !== "string" || targetId === field.id) {
      return false;
    }

    const targetType = typesById.get(targetId);
    if (targetType === undefined || !confirmTargetTypes.has(targetType)) {
      return false;
    }
  }

  return true;
};

const isPublicFormFieldBase = (value: unknown): value is Record<string, unknown> => {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === "string" &&
    typeof value.key === "string" &&
    typeof value.label === "string" &&
    typeof value.required === "boolean" &&
    Number.isInteger(value.rowIndex) &&
    Number.isInteger(value.columnIndex) &&
    isNullableString(value.helpText) &&
    isNullableString(value.placeholder) &&
    fieldTypes.has(String(value.type)) &&
    Object.hasOwn(value, "configJson") &&
    Object.hasOwn(value, "validationJson")
  );
};

const isTextConfig = (value: unknown): boolean => {
  if (value === null) {
    return true;
  }

  return (
    isRecord(value) && hasOnlyKeys(value, ["multiline"]) && typeof value.multiline === "boolean"
  );
};

const isDateTimeConfig = (value: unknown): boolean => {
  return (
    isRecord(value) &&
    hasOnlyKeys(value, ["mode"]) &&
    typeof value.mode === "string" &&
    dateTimeModes.has(value.mode)
  );
};

const isHiddenConfig = (value: unknown): boolean => {
  if (value === null) {
    return true;
  }

  if (!isRecord(value) || !hasOnlyKeys(value, ["defaultValue"])) {
    return false;
  }

  return (
    typeof value.defaultValue === "string" &&
    value.defaultValue.length <= HIDDEN_DEFAULT_VALUE_LIMIT &&
    isJsonbRepresentableString(value.defaultValue)
  );
};

const isConfirmConfig = (value: unknown): boolean => {
  return (
    isRecord(value) &&
    hasOnlyKeys(value, ["targetFieldId"]) &&
    typeof value.targetFieldId === "string" &&
    value.targetFieldId.length > 0
  );
};

const isContentConfig = (value: unknown): boolean => {
  if (!isRecord(value) || !hasOnlyKeys(value, ["document", "version"])) {
    return false;
  }

  return value.version === 1 && isContentDocument(value.document);
};

const isChoiceConfig = (value: unknown): boolean => {
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, ["options", "reservedValues"]) ||
    !Array.isArray(value.options) ||
    value.options.length === 0
  ) {
    return false;
  }

  if (!hasValidUniqueOptions(value.options)) {
    return false;
  }

  return hasValidReservedValues(value);
};

const hasValidUniqueOptions = (options: unknown[]): boolean => {
  const ids = new Set<string>();
  const values = new Set<string>();

  for (const option of options) {
    if (!isRecord(option) || !hasOnlyKeys(option, ["id", "label", "value"])) {
      return false;
    }

    if (
      typeof option.id !== "string" ||
      typeof option.label !== "string" ||
      typeof option.value !== "string"
    ) {
      return false;
    }

    const id = option.id.trim();
    const label = option.label.trim();
    const value = option.value.trim();

    if (!id || !label || !value || ids.has(id) || values.has(value)) {
      return false;
    }

    ids.add(id);
    values.add(value);
  }

  return true;
};

const hasValidReservedValues = (config: Record<string, unknown>): boolean => {
  if (config.reservedValues === undefined) {
    return true;
  }

  if (!Array.isArray(config.reservedValues)) {
    return false;
  }

  const reservedValues = new Set<string>();
  for (const reservedValue of config.reservedValues) {
    if (typeof reservedValue !== "string" || !reservedValue.trim()) {
      return false;
    }

    reservedValues.add(reservedValue.trim());
  }

  const options = config.options as Array<Record<string, unknown>>;

  return options.every((option) => reservedValues.has(String(option.value).trim()));
};

const isLengthValidation = (value: unknown, maximum: number): boolean => {
  if (value === null) {
    return true;
  }

  if (!isRecord(value) || !hasOnlyKeys(value, ["maxLength", "minLength"])) {
    return false;
  }

  const minimum = optionalBoundedInteger(value.minLength, maximum);
  const maximumResult = optionalBoundedInteger(value.maxLength, maximum);

  if (value.minLength === undefined && value.maxLength === undefined) {
    return false;
  }

  if (!minimum.valid || !maximumResult.valid) {
    return false;
  }

  return isOrderedRange(minimum.value, maximumResult.value);
};

const isNumberValidation = (value: unknown): boolean => {
  if (value === null) {
    return true;
  }

  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, ["allowNegative", "integer", "max", "maxDecimalPlaces", "min"])
  ) {
    return false;
  }

  if (
    value.allowNegative === undefined &&
    value.integer === undefined &&
    value.maxDecimalPlaces === undefined &&
    value.min === undefined &&
    value.max === undefined
  ) {
    return false;
  }

  if (value.allowNegative !== undefined && typeof value.allowNegative !== "boolean") {
    return false;
  }

  if (value.integer !== undefined && typeof value.integer !== "boolean") {
    return false;
  }

  if (!isOptionalDecimalPlaces(value.maxDecimalPlaces)) {
    return false;
  }

  if (value.min !== undefined && !isFiniteNumber(value.min)) {
    return false;
  }

  if (value.max !== undefined && !isFiniteNumber(value.max)) {
    return false;
  }

  if (!isNumberDecimalConsistent(value)) {
    return false;
  }

  if (!isNumberNegativeConsistent(value)) {
    return false;
  }

  return isOrderedRange(value.min as number | undefined, value.max as number | undefined);
};

const isOptionalDecimalPlaces = (value: unknown): boolean => {
  if (value === undefined) {
    return true;
  }

  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= NUMBER_MAX_DECIMAL_PLACES_LIMIT
  );
};

// Integer-only numbers cannot also allow fractional decimal places.
const isNumberDecimalConsistent = (value: Record<string, unknown>): boolean => {
  return (
    value.integer !== true || value.maxDecimalPlaces === undefined || value.maxDecimalPlaces === 0
  );
};

// When negatives are disallowed, any configured bound must stay non-negative.
const isNumberNegativeConsistent = (value: Record<string, unknown>): boolean => {
  if (value.allowNegative !== false) {
    return true;
  }

  if (typeof value.min === "number" && value.min < 0) {
    return false;
  }

  if (typeof value.max === "number" && value.max < 0) {
    return false;
  }

  return true;
};

const isWebsiteValidation = (value: unknown): boolean => {
  if (value === null) {
    return true;
  }

  if (!isRecord(value) || !hasOnlyKeys(value, ["httpsOnly", "maxLength", "minLength"])) {
    return false;
  }

  if (value.httpsOnly !== undefined && typeof value.httpsOnly !== "boolean") {
    return false;
  }

  const hasLengthRule = value.maxLength !== undefined || value.minLength !== undefined;
  if (value.httpsOnly === undefined && !hasLengthRule) {
    return false;
  }

  if (!hasLengthRule) {
    return true;
  }

  const lengthRules: LengthRuleRecord = {
    maxLength: value.maxLength,
    minLength: value.minLength,
  };

  return isLengthValidation(lengthRules, 10_000);
};

const isSelectionValidation = (value: unknown): boolean => {
  if (value === null) {
    return true;
  }

  if (!isRecord(value) || !hasOnlyKeys(value, ["maxSelections", "minSelections"])) {
    return false;
  }

  if (value.minSelections === undefined && value.maxSelections === undefined) {
    return false;
  }

  const minimum = optionalBoundedInteger(value.minSelections, Number.MAX_SAFE_INTEGER);
  const maximum = optionalBoundedInteger(value.maxSelections, Number.MAX_SAFE_INTEGER);

  if (!minimum.valid || !maximum.valid) {
    return false;
  }

  return isOrderedRange(minimum.value, maximum.value);
};

const optionalBoundedInteger = (value: unknown, maximum: number): BoundedIntegerResult => {
  if (value === undefined) {
    return { valid: true };
  }

  if (typeof value !== "number") {
    return { valid: false };
  }

  return {
    valid: Number.isInteger(value) && value >= 0 && value <= maximum,
    value,
  };
};

const isOrderedRange = (minimum: number | undefined, maximum: number | undefined): boolean => {
  if (minimum === undefined || maximum === undefined) {
    return true;
  }

  return minimum <= maximum;
};

const hasOnlyKeys = (value: Record<string, unknown>, keys: string[]): boolean => {
  return Object.keys(value).every((key) => keys.includes(key));
};
